import React from "react";
import { Link } from "react-router-dom";
import {
  Globe,
  Zap,
  Server,
  Users,
  Mail,
  Lock,
  Globe2,
  Check,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  ChevronRight,
  HardDrive,
} from "lucide-react";

export default function HostingMegaMenu({ onClose, isMobile = false }) {
  // 7 Modern Core Categories (Mirrors User Request)
  const leftCategories = [
    {
      id: "webhosting",
      name: "Webhosting (NVMe)",
      desc: "Ultra-schneller NVMe SSD Webspace mit LiteSpeed Enterprise & cPanel",
      badge: "NVMe Gen4",
      badgeClass: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
      price: "Ab CHF 9.90 / Mt.",
      icon: Globe,
      iconBox: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
      to: "/webhosting?category=webhosting",
    },
    {
      id: "wordpress",
      name: "WordPress Hosting",
      desc: "Mit LiteSpeed Cache & AccelerateWP optimierte WordPress-Power",
      badge: "LSCache Boost",
      badgeClass: "bg-amber-500/15 text-amber-300 border-amber-500/30",
      price: "Ab CHF 6.90 / Mt.",
      icon: Zap,
      iconBox: "bg-amber-500/15 text-amber-400 border-amber-500/30",
      to: "/webhosting?category=wordpress",
    },
    {
      id: "vps",
      name: "Cloud VPS & VDS",
      desc: "Dedizierte KVM-Kerne, NVMe RAID-10 und 10 Gbit/s Schweizer Uplink",
      badge: "KVM Root Server",
      badgeClass: "bg-blue-500/15 text-blue-300 border-blue-500/30",
      price: "Ab CHF 19.90 / Mt.",
      icon: Server,
      iconBox: "bg-blue-500/15 text-blue-400 border-blue-500/30",
      to: "/webhosting?category=vps",
    },
  ];

  const rightCategories = [
    {
      id: "reseller",
      name: "Reseller Hosting",
      desc: "100% White-Label mit WHM, cPanel und eigener Kundenverwaltung",
      badge: "100% White-Label",
      badgeClass: "bg-gradient-to-r from-[#FF7A00]/25 to-[#E63946]/25 text-[#FF9E40] border-[#FF7A00]/40",
      price: "Ab CHF 79.90 / Mt.",
      icon: Users,
      iconBox: "bg-[#FF7A00]/15 text-[#FF7A00] border-[#FF7A00]/30",
      isFeatured: true,
      to: "/webhosting?category=reseller",
    },
    {
      id: "mail",
      name: "E-Mail Hosting",
      desc: "Professionelle Firmen-Postfächer mit eigener Domain & SpamProtection",
      badge: "SpamProtection",
      badgeClass: "bg-sky-500/15 text-sky-300 border-sky-500/30",
      price: "Ab CHF 4.90 / Mt.",
      icon: Mail,
      iconBox: "bg-sky-500/15 text-sky-400 border-sky-500/30",
      to: "/webhosting?category=mail",
    },
    {
      id: "ssl",
      name: "SSL Zertifikate",
      desc: "Banking-Grade 256-Bit Verschlüsselung & offizielle Vertrauenssiegel",
      badge: "256-Bit SSL",
      badgeClass: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
      price: "Inklusive / CHF 25",
      icon: Lock,
      iconBox: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
      to: "/webhosting?category=ssl",
    },
    {
      id: "domains",
      name: "Domain-Preise",
      desc: "Übersicht aller Schweizer & internationaler Top-Level-Domains",
      badge: ".ch ab CHF 15.-",
      badgeClass: "bg-purple-500/15 text-purple-300 border-purple-500/30",
      price: "Live-Check",
      icon: Globe2,
      iconBox: "bg-purple-500/15 text-purple-400 border-purple-500/30",
      to: "/webhosting?category=domains",
    },
  ];

  const allCategories = [...leftCategories, ...rightCategories];

  // Bottom Ticker Benefits
  const benefitTicks = [
    "LiteSpeed Enterprise",
    "100% NVMe Gen4 SSD",
    "100% White-Label WHM",
    "Kostenloses Auto-SSL",
    "Schweizer DDoS-Schutz",
    "24/7 Schweizer Support",
    "99.9% Uptime SLA",
  ];

  // ============================================================================
  // MOBILE VIEW
  // ============================================================================
  if (isMobile) {
    return (
      <div className="space-y-1.5 pt-1">
        {allCategories.map((item) => {
          const IconComp = item.icon;
          return (
            <Link
              key={item.id}
              to={item.to}
              onClick={onClose}
              className="flex items-center justify-between p-2.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.08] border border-white/5 transition"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border ${item.iconBox}`}>
                  <IconComp size={14} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-bold text-white truncate">{item.name}</div>
                  <div className="text-[10px] text-slate-400 line-clamp-1">{item.desc}</div>
                </div>
              </div>
              <span className="text-[11px] font-mono font-bold text-[#D4AF37] shrink-0 ml-2">
                {item.price}
              </span>
            </Link>
          );
        })}
        <Link
          to="/webhosting"
          onClick={onClose}
          className="flex items-center justify-center gap-1.5 p-2.5 rounded-xl text-xs font-bold text-slate-300 hover:text-white hover:bg-white/5 transition mt-1"
        >
          <span>Alle Hosting-Tarife ansehen</span>
          <ArrowRight size={13} />
        </Link>
      </div>
    );
  }

  // ============================================================================
  // DESKTOP LUXURY MEGA MENU (Ultra-Modern Glassmorphism Card)
  // ============================================================================
  return (
    <div
      role="menu"
      aria-label="Webhosting Dropdown"
      className="w-[860px] max-w-[95vw] mx-auto rounded-[28px] bg-[#07090E] text-white shadow-[0_30px_90px_rgba(0,0,0,0.95)] border border-white/15 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
    >
      {/* Top Banner Header */}
      <div className="flex items-center justify-between px-6 py-3.5 bg-gradient-to-r from-white/[0.04] via-white/[0.02] to-white/[0.04] border-b border-white/10 text-xs">
        <div className="flex items-center gap-2 font-mono text-[11px] text-slate-300">
          <span className="flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="font-bold text-white">🇨🇭 100% Serverstandort Schweiz</span>
          <span className="text-slate-500">•</span>
          <span className="text-slate-400">Tier-IV Datacenter Zürich • ISO 27001</span>
        </div>
        <Link
          to="/webhosting"
          onClick={onClose}
          className="font-bold text-xs text-[#E63946] hover:text-[#ff5c6a] flex items-center gap-1 transition group"
        >
          <span>Hosting-Übersicht öffnen</span>
          <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>

      {/* Main 2-Column Grid */}
      <div className="grid grid-cols-12 p-6 gap-6">
        {/* ================================================== */}
        {/* LEFT COLUMN: 3 Core Hosting Categories             */}
        {/* ================================================== */}
        <div className="col-span-6 pr-2 border-r border-white/10 space-y-2">
          <div className="px-2 pb-1 flex items-center justify-between">
            <span className="text-[11px] font-black uppercase tracking-wider text-cyan-400">
              WEBHOSTING & SERVER
            </span>
            <span className="text-[10px] text-slate-400 font-mono">NVMe PCIe 4.0</span>
          </div>

          <div className="space-y-2">
            {leftCategories.map((item) => {
              const IconComp = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  onClick={onClose}
                  className="group flex items-start gap-3.5 p-3 rounded-2xl bg-white/[0.02] hover:bg-white/[0.06] border border-white/5 hover:border-white/15 transition-all duration-200 hover:-translate-y-0.5 shadow-sm"
                >
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${item.iconBox} transition-transform duration-200 group-hover:scale-110 shadow-md mt-0.5`}
                  >
                    <IconComp size={18} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-black text-white group-hover:text-[#E63946] transition-colors">
                          {item.name}
                        </span>
                        {item.badge && (
                          <span
                            className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-full border ${item.badgeClass} shrink-0`}
                          >
                            {item.badge}
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] font-mono font-bold text-slate-400 group-hover:text-white shrink-0">
                        {item.price}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 leading-snug mt-1 group-hover:text-slate-300">
                      {item.desc}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* ================================================== */}
        {/* RIGHT COLUMN: Reseller (Featured) + Mail, SSL, TLDs*/}
        {/* ================================================== */}
        <div className="col-span-6 pl-1 space-y-2">
          <div className="px-2 pb-1 flex items-center justify-between">
            <span className="text-[11px] font-black uppercase tracking-wider text-[#FF7A00]">
              AGENTUREN, MAIL & SECURITY
            </span>
            <span className="text-[10px] text-slate-400 font-mono">Schweizer SLA</span>
          </div>

          <div className="space-y-2">
            {rightCategories.map((item) => {
              const IconComp = item.icon;
              const isReseller = item.id === "reseller";

              return (
                <Link
                  key={item.id}
                  to={item.to}
                  onClick={onClose}
                  className={`group flex items-start gap-3.5 p-3 rounded-2xl transition-all duration-200 hover:-translate-y-0.5 shadow-sm ${
                    isReseller
                      ? "bg-gradient-to-r from-[#FF7A00]/10 via-amber-500/5 to-transparent border border-[#FF7A00]/30 hover:border-[#FF7A00]/60 shadow-[0_4px_20px_rgba(255,122,0,0.1)]"
                      : "bg-white/[0.02] hover:bg-white/[0.06] border border-white/5 hover:border-white/15"
                  }`}
                >
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${item.iconBox} transition-transform duration-200 group-hover:scale-110 shadow-md mt-0.5`}
                  >
                    <IconComp size={18} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-black transition-colors ${isReseller ? "text-amber-200 group-hover:text-amber-100" : "text-white group-hover:text-[#E63946]"}`}>
                          {item.name}
                        </span>
                        {item.badge && (
                          <span
                            className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-full border ${item.badgeClass} shrink-0`}
                          >
                            {item.badge}
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] font-mono font-bold text-slate-400 group-hover:text-white shrink-0">
                        {item.price}
                      </span>
                    </div>
                    <p className={`text-[11px] leading-snug mt-1 ${isReseller ? "text-amber-300/80 font-medium group-hover:text-amber-200" : "text-slate-400 group-hover:text-slate-300"}`}>
                      {item.desc}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* ================================================== */}
      {/* BOTTOM TICKER BANNER (Jrodix Style)                */}
      {/* ================================================== */}
      <div className="bg-white/[0.03] border-t border-white/10 px-6 py-3.5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap text-[11px] text-slate-300 font-medium">
          {benefitTicks.map((tick, tIdx) => (
            <span key={tIdx} className="flex items-center gap-1.5">
              <Check size={12} className="text-emerald-400 font-black" />
              <span>{tick}</span>
            </span>
          ))}
        </div>

        <Link
          to="/webhosting#kategorien"
          onClick={onClose}
          className="text-[11px] font-black text-[#D4AF37] hover:underline flex items-center gap-1 shrink-0"
        >
          <span>Alle Tarife ansehen</span>
          <ChevronRight size={13} />
        </Link>
      </div>
    </div>
  );
}
